import {IMaterialTooltipPlacement, classNames, React, _, useCallback, observer, useAsObservableSource, useLocalStore, Fragment, Popover2} from '@api'
import {Icon} from '@blueprintjs/core'
import {Tooltip} from '@material-ui/core'
import {Fullscreen, ViewColumn} from '@material-ui/icons'
import {BuilderCardStore, C8MenuButton, ErrorBoundary, useBuilderCardStore, WhyNoPublishPopoverPanel} from '@modules'
import {icons} from '@stores/ui'
import {computed, toJS} from 'mobx'
import * as css from './BuilderCardButtons.css'
import * as cardCss from './BuilderCard.css'
import {useCatalog} from '@stores'
import {PrimaryIconButton} from 'centric-ui-components'
import {Tooltip2} from '@api'

class BuilderCardButtonsStore {
  constructor(public props: { inline: boolean; isSelected: boolean, store: BuilderCardStore }) {

  }

  @computed get buttonTooltipPlacement(): IMaterialTooltipPlacement | any {
    const {props: {store: {props: {isPopover}}, inline}} = this
    return isPopover || inline ? 'bottom' : null
  }
}

const BuilderCardButtonsStoreContext = React.createContext<BuilderCardButtonsStore>(null)
const useBuilderCardButtonsStore = () => React.useContext(BuilderCardButtonsStoreContext)

export const BuilderCardButtons = observer<{ inline?: boolean, isSelected?: boolean, className?: string }>(
  function BuilderCardButtons({inline, isSelected, className}) {
    const store = useBuilderCardStore(),
      {
        imagesToRender, vlp, levelOfDetail, props: {popoverParent, isPopover}, data, copyDbString,
        catalog: {dialogs, selection, settings: {imageRatio}, isActionButtonEnabled, stores: {deployment: {settings: {allowReimportImage}}}}
      } = store,
      captureDoubleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
      }, []),
      captureClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
      }, []),
      obsProps = useAsObservableSource(({inline, isSelected, store})),
      myStore = useLocalStore(() => new BuilderCardButtonsStore(obsProps))

    return <BuilderCardButtonsStoreContext.Provider value={myStore}>
      <div className={classNames(css.buttons, className, isSelected && selection?.selectedIds?.size == 1 && css.showButtons)}
           data-pos='right'
           data-image-ratio-type={imageRatio}
           data-level={levelOfDetail}
           onClick={captureClick}
           onDoubleClick={captureDoubleClick}
      >
        {isActionButtonEnabled('toggleActive', data) && <ToggleActiveCardButton/>}
        {(inline || levelOfDetail === 1) && popoverParent != 'searcher' && <DuplicateCatalogItemCardButton/>}
        {(inline || levelOfDetail === 1) && isActionButtonEnabled('delete', data) && <DeleteCatalogItemCardButton/>}
        {DEBUG &&
			<PrimaryIconButton
          title={`${copyDbString ? 'Copied' : 'Copy'} Query to Clipboard`}
				icon={<Icon icon={copyDbString ? 'tick' : 'database'}/>}
				onClick={store.copyDocQueryToClipboard}
			/>
        }
        <C8CardButton/>
        <ToggleStackSpreadCardButton/>
        <PrimaryIconButton
          title={`Refresh Images`}
          icon={<Icon icon={'image-rotate-right'}/>}
          disabled={store.refreshingImages}
          onClick={store.onRefreshImages}
        />

      </div>

      <div className={classNames(css.buttons, className, isSelected && selection?.selectedIds?.size == 1 && css.showButtons)}
           data-pos='left'
           data-image-ratio-type={imageRatio}
           data-level={levelOfDetail}
           onClick={captureClick}
           onDoubleClick={captureDoubleClick}>
        {popoverParent != 'searcher' && !isPopover && <ToggleInlineDetailsCardButton/>}
        <CardDetailsCardButton/>
        {!inline && <GoToSeasonCardButton/> }
        {popoverParent != 'searcher' && <UploadButton/>}
        {popoverParent != 'searcher' && <FullScreenImageCardButton/>}


        {/* Only show seasonal info if we're not in that season already */}
        {!vlp?.isVLPView && !dialogs.showQrCodes &&
		 	 <div>
				 <PrimaryIconButton data-name='qr-code'
				                    data-id='qr-code'
                            title='QR Code'
				                    active={store.showQRCode}
				                    onClick={e => {
                                      e.stopPropagation()
                                      store.showQRCode = !store.showQRCode
                                      if (store.showQRCode) {
                                        store.hideButtons = true
                                        setTimeout(() => store.hideButtons = false, 3000)
                                      }
                                    }}><Icon icon={'code'}/></PrimaryIconButton>
			 </div>}


      </div>

      {false && !inline && imagesToRender.length > 0 && <div className={classNames(css.buttons, className)}
	                                                data-pos='bottom'
	                                                data-image={imageRatio}
	                                                data-level={levelOfDetail}
	  >
		  <BuilderCardImageCarouselButtons/>
	  </div>
      }
    </BuilderCardButtonsStoreContext.Provider>
  })

BuilderCardButtons.displayName = 'BuilderCardButtons'
BuilderCardButtons['whyDidYouRender'] = true

export const NavigateImageCarouselButtons = observer<{className?: string}>(function NavigateImageCarouselButtons({className}) {
    const {imagesToRender, catalog: {settings: {imageRatio}}, levelOfDetail} = useBuilderCardStore()

    return (<Fragment>
      {imagesToRender.length > 0 && <div className={classNames(css.buttons, className)}
                                                    data-pos='bottom'
                                                    data-image={imageRatio}
                                                    data-level={levelOfDetail}
      >
	      <BuilderCardImageCarouselButtons/>
      </div>
      }
    </Fragment>)
})
export const BuilderCardImageCarouselButtons = observer(function BuilderCardImageCarouselButtons() {
  const {imagesToRender, imageSourceView} = useBuilderCardStore()

  if (imagesToRender.length <= 1 || imageSourceView != 'first') return null

  return (<Fragment>
    <PreviousImageButton/>
    <NextImageButton/>
  </Fragment>)
})

const PreviousImageButton = observer(function PreviousImageButton() {
  const {imagesToRender, selectedImageIndex, maxAllowedSelectedImageIndex, selectPreviousImage} = useBuilderCardStore()

  if (imagesToRender.length <= 1) return null

  return (<Fragment>
    <PrimaryIconButton wrapperClassName={cardCss.previousImageButton} icon='arrow-left' tooltipPosition='bottom' onClick={selectPreviousImage}
                       tooltip={`Previous Image\n(${selectedImageIndex == 0 ? maxAllowedSelectedImageIndex + 1 : selectedImageIndex + 1} / ${imagesToRender.length})`}
    />
  </Fragment>)
})

const NextImageButton = observer(function PreviousImageButton() {
  const {imagesToRender, selectedImageIndex, selectNextImage, maxAllowedSelectedImageIndex} = useBuilderCardStore()

  if (imagesToRender.length <= 1) return null

  return (<Fragment>
    <PrimaryIconButton wrapperClassName={cardCss.nextImageButton} icon='arrow-right' tooltipPosition='bottom'
                       tooltip={`Next Image\n(${selectedImageIndex === maxAllowedSelectedImageIndex ? 1 : selectedImageIndex + 2} / ${imagesToRender.length})`}
                       onClick={selectNextImage}/>
  </Fragment>)
})

export type lod0CardButtonLocation = 'tr' | 'tl' | 'br' | 'bl'

export const GoToSeasonCardButton = observer<{ className?: string, location?: lod0CardButtonLocation }>(function GoToSeasonCardButton({className, location = 'br'}) {
  const {navigateToSeason, seasonSelectorFieldsNonNull, seasonsValueToNavToItem, levelOfDetail} = useBuilderCardStore(),
    {buttonTooltipPlacement} = useBuilderCardButtonsStore() ?? {buttonTooltipPlacement: null},
    fieldValues = seasonSelectorFieldsNonNull.map(({field}, i) => _.get(seasonsValueToNavToItem, field)).filter(v => v && v !== ''),
    disabled = _.isEmpty(fieldValues)

  // console.log(toJS({seasonSelectorFieldsNonNull, seasonsValueToNavToItem, fieldValues}, {recurseEverything: true}))

  // if (disabled) return null

  return (
    <Tooltip placement={buttonTooltipPlacement ?? 'right'}
             title={_.isEmpty(seasonSelectorFieldsNonNull) ? 'No Seasonal Info' : disabled ? 'Already in Season' :
                    <ErrorBoundary>
                        <div className={css.goToSeasonTooltip}>
                      Navigate to:
                      <br/>
                      <div className={css.seasonBreadcrumbs}>
                        {fieldValues.map((v, i) => <span className={css.seasonBreadcrumb} key={i}>{v}</span>)}
                      </div>
                    </div>
                    </ErrorBoundary>}>
      <PrimaryIconButton disabled={disabled} wrapperClassName={className} data-location={location} onClick={navigateToSeason}>
        <Icon icon={icons.goToSeason}/>
      </PrimaryIconButton>
    </Tooltip>)
})

export const HoverCardButtons = observer(function HoverCardButtons() {
  const {
    levelOfDetail,
    props: {isPopover, nested}, catalog: {isActionButtonEnabled}, data,
    showHoverButtons, canDuplicate, canDelete
  } = useBuilderCardStore(),
    buttons = [
      'inline-details',
      isActionButtonEnabled('toggleActive', data) && 'toggle-active',
      canDelete && 'duplicate',
      canDuplicate && 'duplicate'
    ].filter(_.identity)

  return !isPopover && levelOfDetail == 0 && !nested?.isInCardField && showHoverButtons &&
         <Fragment>
	         <ToggleInlineDetailsCardButton className={cardCss.inlineDetailsToggleButton} location='tl'/>
	         <ToggleCardPopoverButton className={cardCss.popoverToggleButton} location='bl'/>
	         <GoToSeasonCardButton location='br' className={!canDuplicate ? css.duplicateButton : css.deleteButton}/>
           {isActionButtonEnabled('toggleActive', data) && <ToggleActiveCardButton location='tr'/>}
           {false && <Fragment>
           {canDuplicate && <DuplicateCatalogItemCardButton className={cardCss.duplicateButton} location='tr' />}
           {canDelete && <DeleteCatalogItemCardButton className={cardCss.deleteButton} location='br'/>}
           </Fragment>}

	         {/*<ToggleStackSpreadCardButton className={css.toggleStackSpread}/>*/}

	         <BuilderCardImageCarouselButtons/>
         </Fragment>
})

export const ToggleActiveCardButton = observer<{ location?: lod0CardButtonLocation}>(function ToggleActiveCardButton({location}) {
  const {isActive, onToggleActive, showToggleActive, strings, catalog} = useBuilderCardStore(),
    {buttonTooltipPlacement} = useBuilderCardButtonsStore() ?? {buttonTooltipPlacement: 'bottom'}

  // if (nestedObject) {
  //   if (perspective.activeField) {
  //     active = es.readField(nestedObject, perspective.activeField).val
  //   }
  // }

  return !showToggleActive || isActive === null ? null :
           <PrimaryIconButton
             data-name='toggle-active-deactive'
             onClick={onToggleActive}
             tooltip={strings.toggleActive(catalog, isActive, false)}
             data-location={location}
             // active={!isActive}
             icon={<Icon icon={!isActive ? icons.active : icons.inactive}/>}
           />
})

const UploadButton = observer(function UploadButton() {
  const {isOffline, strings, uploadToC8, levelOfDetail, props: {isPopover}, canPublish} = useBuilderCardStore(),
    {buttonTooltipPlacement} = useBuilderCardButtonsStore() ?? {buttonTooltipPlacement: 'bottom'}

  return !isOffline ? null : (
      <Popover2
        interactionKind='hover'
        disabled={canPublish || levelOfDetail != 1 || isPopover}
        position={buttonTooltipPlacement ?? 'left'}
        minimal={false}
        content={<WhyNoPublishPopoverPanel/>}
      >
        <PrimaryIconButton
          data-name='toggle-active'
          disabled={!canPublish}
          onClick={uploadToC8}
          tooltip={strings.uploadToC8}
          icon={<Icon icon={'cloud-upload'}/>}
        />
      </Popover2>
  )
})

const CardDetailsCardButton = observer(function CardDetailsCardButton() {
  const {isShowingDetails, strings, toggleCatalogItemDetails} = useBuilderCardStore(),
    {buttonTooltipPlacement} = useBuilderCardButtonsStore() ?? {buttonTooltipPlacement: 'bottom'}

  return (
    <PrimaryIconButton
      data-name='spec-details'
      active={isShowingDetails}
      tooltip={strings.itemDetails}
      onClick={toggleCatalogItemDetails}
      icon={<Icon icon={'list-detail-view'}/>}
    />)
})

export const DuplicateCatalogItemCardButton = observer<{ className?: string, location?: lod0CardButtonLocation }>(function DuplicateCatalogItemCardButton({className}) {
  const {cardTitle, onDuplicate, canDuplicate, strings} = useBuilderCardStore(),
    {buttonTooltipPlacement} = useBuilderCardButtonsStore() ?? {buttonTooltipPlacement: 'right'}

  if (!canDuplicate) return null

  return (
    <PrimaryIconButton
      tooltipPosition={buttonTooltipPlacement ?? 'right'}
      tooltip={`${strings.duplicate} ${cardTitle}`}
      wrapperClassName={className}
      data-name='duplicate-products'
      onClick={onDuplicate}
      icon={<Icon icon={'duplicate'}/>}
    />
  )
})

const FullScreenImageCardButton = observer(function FullScreenImageCardButton() {
  const {toggleCatalogItemDetails, images, strings} = useBuilderCardStore(),
    {buttonTooltipPlacement} = useBuilderCardButtonsStore() ?? {buttonTooltipPlacement: 'bottom'}

  return (images?.length > 0 &&
          <PrimaryIconButton
	          tooltipPosition={buttonTooltipPlacement ?? 'right'}
	          tooltip={strings.fullscreenImage}
	          icon={<Fullscreen fontSize='large'/>}
	          data-name='fullscreen'
	          className={css.fullscreen}
	          onClick={(e) => toggleCatalogItemDetails(e, true, true)}
          />
  )
})

const C8CardButton = observer(function C8CardButton() {
  const store = useBuilderCardStore(),
    {data, props: {inlinePopover}, catalog: {c8, openInC8Field, catalogConfig: {redirectToPLM}}} = store,
    {props: {inline}} = useBuilderCardButtonsStore() ?? {props: {inline: false}},
    openInC8Value = openInC8Field ? _.get(data, openInC8Field) : null

  return openInC8Value ? <C8MenuButton className={css.c8Button} url={c8.urlForId(openInC8Value, redirectToPLM)} tooltipPlacement={inlinePopover || inline ? 'bottom' : 'right'}/> : null
})

export const DeleteCatalogItemCardButton = observer<{ className?: string, location?: lod0CardButtonLocation }>(function DeleteCatalogItemCardButton({className}) {
  const {onDelete, cardTitle, isOffline, data, catalog: {isActionButtonEnabled}, strings} = useBuilderCardStore(),
    {buttonTooltipPlacement} = useBuilderCardButtonsStore() ?? {buttonTooltipPlacement: 'bottom'},
    deleteBtn = isActionButtonEnabled('delete', data)

  return !deleteBtn && !isOffline ? null :
         <PrimaryIconButton
           tooltip={`${strings.delete} ${cardTitle}`}
           tooltipPosition={buttonTooltipPlacement ?? 'right'}
           data-name='delete-product'
           wrapperClassName={className}
           onClick={onDelete}
           icon={<Icon icon={'trash'}/>}
         />

})

export const ToggleStackSpreadCardButton = observer<{ className?: string, location?: lod0CardButtonLocation }>(function ToggleStackSpreadCardButton({className}) {
  const {settings: {imageSource: {view}}} = useCatalog(),
    {unstackCards, onToggleUnstackCards, imagesToRender} = useBuilderCardStore(),
    {buttonTooltipPlacement} = useBuilderCardButtonsStore() ?? {buttonTooltipPlacement: 'bottom'}

  if (view !== 'stack' || imagesToRender.length <= 1) return null

  return (
    <PrimaryIconButton
      data-name='stack-cards'
      tooltip={`Unstack Cards`}
      tooltipPosition={buttonTooltipPlacement ?? 'right'}
      wrapperClassName={className}
      active={unstackCards}
      onClick={onToggleUnstackCards}
      icon={<ViewColumn/>}
    />)
})

export const ToggleInlineDetailsCardButton = observer<{ className?: string, location?: lod0CardButtonLocation }>(function ToggleInlineDetailsCardButton({className}) {
  const {localLOD, levelOfDetail, toggleLocalLOD, strings} = useBuilderCardStore(),
    {buttonTooltipPlacement} = useBuilderCardButtonsStore() ?? {buttonTooltipPlacement: 'left'}

  return <PrimaryIconButton
    tooltipPosition={buttonTooltipPlacement ?? 'left'}
    tooltip={strings.toggleInlineDetails}
    active={levelOfDetail === 1}
    wrapperClassName={className}
    onClick={toggleLocalLOD}
    icon={<Icon icon='arrows-vertical'/>}
  />
})

export const ToggleCardPopoverButton = observer<{ className?: string, location?: lod0CardButtonLocation }>(function ToggleCardPopoverButton({className}) {
  const {levelOfDetail, strings, props: {isPopover}, showDetailsPopoverSettings: {interactionKind, ms}, isShowingPopover, togglePopover, onPopoverTooltipClose, onPopoverTooltipOpen} = useBuilderCardStore(),
    tooltipTitle = `Popover ${strings.itemDetails}${interactionKind === 'hover'
                                                    ? `(Hover to Show)`
      // ? `${isShowingPopover || !showingOrHidingPopoverInMs 
      //      ? '' 
      //      : `\n(Showing in ${(ms / 1000).toFixed(1)}s)`}` 
                                                    : `\n(Click to ${isShowingPopover ? 'Hide' : 'Show'}`})`

  return levelOfDetail === 1 || isPopover ? null :
         <Tooltip placement={'left'}
                   title={tooltipTitle}
                   onOpen={onPopoverTooltipOpen}
                   onClose={onPopoverTooltipClose}
         >
           <PrimaryIconButton
             wrapperClassName={className}
             active={isShowingPopover}
             //onBlur={togglePopover}
             onClick={interactionKind === 'hover' ? null :togglePopover}
             icon={<Icon icon='style'/>}
           />
         </Tooltip>
})




// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/BuilderCardButtons.tsx